/* eslint-disable */
/**
 * coLivingBathSplit.js — private vs shared bedroom rent, and the bed ceiling that pays
 * for it, re-solved live.
 *
 * A bathroom you can only enter through a bedroom (an ENSUITE, or a Jack-and-Jill
 * between two) poses one question: do you keep it private, or add a door to the hall —
 * sometimes a stud wall to carve a corridor — so the whole house can use it? That is a
 * CHOICE, not a fact about the drawing, so the analyst declares it per bath on the
 * bathroom cards (default from the company policy), and this module prices the answer.
 *
 * Both halves move together, which is the point:
 *   • KEEP IT PRIVATE — that bedroom rents at PadSplit's private-bath rate, but the bath
 *     is out of the shared pool. PadSplit's rule is per-bathroom ("no more than four
 *     members share one bathroom"), so it now carries 1 tenant instead of maxBedsPerBath.
 *     The house holds fewer beds. The ceiling drops.
 *   • RE-DOOR IT — no premium, but the bath carries the whole house and the ceiling
 *     stands. This is the default, and it's what our bed ceiling has always silently
 *     assumed (it counted every full bath at maxBedsPerBath, reachable or not).
 *
 * Re-solving on every change is not optional. The pipeline folds the split into ONE
 * blended $/room so `beds × ratePerRoom == monthlyGrossRent` holds everywhere
 * (functions/coLiving/coLivingGrossRent.js) — and that blend is only valid at the bed
 * count AND the declarations it was struck at. Re-blending a stale rate across a new room
 * count fabricates a premium on rooms that have no private bath.
 *
 * This is a deliberate MIRROR of functions/coLiving/bathCapacity.js +
 * coLivingGrossRent.js (same convention as coLivingUnderwriting.js mirroring
 * functions/utils/coLivingReturns.js). Change one, change the other;
 * _testCoLivingBathSplit.js pins the shared case table.
 *
 * The two RATES stay editable — that mirrors PadSplit's own host earnings calculator,
 * which asks for "rooms with shared bathroom" and "rooms with private bathroom" as
 * separate count × rate lines rather than one blended figure.
 */

const num = (v) => (typeof v === 'number' && isFinite(v)) ? v : null;

/**
 * Rate-override precedence, in one place because three surfaces need it identically
 * (LeadDetailV2's calculator, DealPresentation's buyer calculator, LeadBuyerMatches'
 * cash-flow warning) and a disagreement between them means a buyer is quoted a rent
 * the wholesaler never saw.
 *
 * `coLivingRatePerRoom` is the LEGACY flat override and keeps its original meaning —
 * every room at that rate. It's applied to BOTH groups, which collapses the premium
 * (private is no longer above shared). The per-group overrides supersede it; the
 * calculator clears the flat field when it writes one, so a lead migrates on first touch.
 *
 * Mirrors functions/coLiving/coLivingGrossRent.js#resolveCoLivingHeadlineRent.
 */
export const resolveRateOverrides = (calc = {}) => {
    const c = calc || {};
    const hasGroup = num(c.coLivingSharedRate) != null || num(c.coLivingPrivateRate) != null;
    const flat = !hasGroup ? num(c.coLivingRatePerRoom) : null;
    return {
        flat,
        sharedRateOverride: flat ?? num(c.coLivingSharedRate),
        privateRateOverride: flat ?? num(c.coLivingPrivateRate),
    };
};

/**
 * What a room-rate blur should actually WRITE. Pure, so the rule is testable — the two
 * bugs this replaced were both invisible-by-inspection state bugs in the blur callbacks:
 * a drive-by focus persisting a market rate as a permanent override, and a half-written
 * migration that resurrected a premium the user had priced away.
 *
 * Three rules:
 *   • An edit is an edit; FOCUS IS NOT. Unchanged value ⇒ write nothing. Without this,
 *     tabbing through the box freezes today's market rate into the lead forever.
 *   • Retiring the legacy flat rate is LOSSLESS. It governed BOTH groups, so we write
 *     both (each box is seeded to the flat value) and clear it in one go. Writing only
 *     the edited box would leave the other unset — and an unset group falls back to the
 *     PIPELINE rate, resurrecting the very premium the flat rate suppressed.
 *   • `persistedFlat` must be the flat rate AS STORED (null when the lead never had one),
 *     never a display fallback — or every lead looks legacy and gets both rates pinned.
 *
 * @param {Object} p
 * @param {'shared'|'private'} p.edited  which box blurred
 * @param {number|null} p.sharedValue    current parsed value of the shared box
 * @param {number|null} p.privateValue   current parsed value of the private box
 * @param {number|null} p.persistedShared  last-persisted coLivingSharedRate
 * @param {number|null} p.persistedPrivate last-persisted coLivingPrivateRate
 * @param {number|null} p.persistedFlat    stored coLivingRatePerRoom (null ⇒ nothing to retire)
 * @returns {Array<{field:string, value:number|null}>} writes to apply, in order (may be empty)
 */
export const planRoomRateWrites = ({
    edited,
    sharedValue = null,
    privateValue = null,
    persistedShared = null,
    persistedPrivate = null,
    persistedFlat = null,
}) => {
    const isShared = edited === 'shared';
    const next = isShared ? sharedValue : privateValue;
    const prev = isShared ? persistedShared : persistedPrivate;
    if (next === prev) return [];                       // focus, not an edit

    if (persistedFlat != null) {                        // retire the flat rate, losslessly
        return [
            { field: 'coLivingSharedRate', value: sharedValue },
            { field: 'coLivingPrivateRate', value: privateValue },
            { field: 'coLivingRatePerRoom', value: null },
        ];
    }
    return [{ field: isShared ? 'coLivingSharedRate' : 'coLivingPrivateRate', value: next }];
};

// ── The capacity rule — mirror of functions/coLiving/bathCapacity.js ──────────

export const PLAN_SHARED = 'shared';
export const PLAN_PRIVATE = 'private';

// Baths already off the hall have nothing to decide, and 'unknown' means we couldn't read
// the door — a missing read must never manufacture a private-bath claim OR a ceiling cut.
export const DECIDABLE_ACCESS = new Set(['ensuite', 'jack_and_jill']);

const coercePlan = (v) => (v === PLAN_PRIVATE || v === PLAN_SHARED) ? v : null;

/**
 * Stamp every bath with the plan in force. Precedence: the analyst's per-bath toggle
 * (calculatorInputs.coLivingBathPlan) → the plan stamped at ingestion from the company
 * policy → 'shared'.
 */
export const resolveBathPlans = (bathAccess, { overrides = null, policy = null } = {}) => {
    if (!Array.isArray(bathAccess)) return [];
    const ov = (overrides && typeof overrides === 'object') ? overrides : {};
    const fallback = coercePlan(policy) || PLAN_SHARED;
    return bathAccess.map((b) => {
        if (!b) return b;
        if (!DECIDABLE_ACCESS.has(b.access)) return { ...b, plan: PLAN_SHARED };
        return { ...b, plan: coercePlan(ov[b.roomId]) ?? coercePlan(b.plan) ?? fallback };
    });
};

/** Tenants ONE bathroom carries. A private bath can never beat a shared one. */
export const bathCapacity = (bath, maxBedsPerBath) => {
    if (!bath) return 0;
    if (bath.fixture === 'half') return 0;              // no shower ⇒ no sleeping capacity
    if (bath.plan !== PLAN_PRIVATE) return maxBedsPerBath;
    const served = Array.isArray(bath.servedRooms) ? bath.servedRooms.length : 0;
    return Math.min(Math.max(1, served), maxBedsPerBath); // 1 ensuite · 2 Jack-and-Jill
};

/**
 * The house's bed ceiling under the plans in force.
 *
 * THE LISTING IS THE AUTHORITY; THE PLAN MAY ONLY REDUCE IT. Two halves of one rule:
 *
 *   • No listing bath count ⇒ NO CAP, even when the plan drew bathrooms. This has always
 *     failed open rather than silently cap; deriving a ceiling from the drawn rooms would
 *     invent a limit where there was none (capping a house at 4 beds off a plan that
 *     happened to draw one bathroom) and can flip a buy-box gate.
 *   • Never ABOVE the listing ⇒ clamp the per-bath sum to maxBedsPerBath × fullBaths. A
 *     plan can draw MORE bathroom rooms than the house has bathrooms (a compartmented
 *     suite is one bathroom in two rooms), and summing M per DRAWN room then claims 12
 *     beds on a 2-bath house — which 148247789's persisted list would have done.
 *
 * So the bath list only ever takes capacity AWAY, by holding a bath private. It can never
 * manufacture capacity, and it can never manufacture a limit.
 */
export const bathCeilingFrom = (plans, { maxBedsPerBath, bathsAdded = 0, fullBaths = null } = {}) => {
    const M = num(maxBedsPerBath);
    if (M == null || M <= 0) return null;
    const adds = Math.max(0, Math.floor(num(bathsAdded) ?? 0));
    const fb = num(fullBaths);
    // No resolvable bath count ⇒ no cap, whatever the plan drew. Fail open, as always.
    if (fb == null || fb <= 0) return null;
    const listingCeiling = M * fb;

    if (!Array.isArray(plans) || !plans.length) return listingCeiling + adds * M;

    const base = plans.reduce((sum, b) => sum + bathCapacity(b, M), 0);
    if (base <= 0) return null;                          // every bath is a half bath
    return Math.min(base, listingCeiling) + adds * M;
};

/**
 * Bedrooms that rent at the private rate. DISTINCT bedrooms, not baths — a primary suite
 * with two ensuite baths is still ONE private-bath room. A Jack-and-Jill earns nothing
 * (semi-private to two tenants is not a PadSplit private bath), and neither does a
 * private HALF bath (the tenant still shares a shower).
 */
export const countPrivateRooms = (plans) => {
    if (!Array.isArray(plans)) return 0;
    const bedrooms = new Set();
    for (const b of plans) {
        if (!b || b.plan !== PLAN_PRIVATE || b.access !== 'ensuite' || b.fixture === 'half') continue;
        const servedId = Array.isArray(b.servedRooms) && b.servedRooms.length ? b.servedRooms[0] : (b.roomId ?? null);
        bedrooms.add(servedId != null ? String(servedId) : `bath:${bedrooms.size}`);
    }
    return bedrooms.size;
};

/**
 * How many bedrooms COULD rent private if the investor kept their bath — independent of
 * what's declared. This is what tells the UI whether a private/shared rate line is even
 * relevant to this house.
 */
export const countEnsuiteBedrooms = (bathAccess) => {
    if (!Array.isArray(bathAccess)) return 0;
    const ids = new Set();
    for (const b of bathAccess) {
        if (!b || b.access !== 'ensuite' || b.fixture === 'half') continue;
        const servedId = Array.isArray(b.servedRooms) && b.servedRooms.length ? b.servedRooms[0] : (b.roomId ?? null);
        ids.add(servedId != null ? String(servedId) : `bath:${ids.size}`);
    }
    return ids.size;
};

/**
 * The baths we may reason from. Fail open on `bathCountMismatch`: the plan drew bath rooms
 * we couldn't reconcile with the listing's count, so the list may be missing a bath or
 * inventing one. Each is still a real room on the plan (the cards stand), but nothing may
 * be DERIVED from it — the ceiling falls back to the listing's full-bath count.
 *
 * Mirrors coLivingGrossRent.js#planBathsFor.
 */
export const planBathsFor = (interior, overrides = null, policy = null) => {
    const cl = interior || {};
    if (cl.bathCountMismatch === true) return [];
    return resolveBathPlans(cl.bathAccess, { overrides, policy });
};

/**
 * Which rooms are priced PRIVATE at this room count. Returns null when there's no split
 * to make — no bath kept private, or no priced premium — and the caller prices every room
 * shared.
 *
 * No surplus test: the beds a private bath costs are already gone from the ceiling
 * (bathCeilingFrom), so charging the premium here isn't double-counting — it's the other
 * half of the trade the investor declared.
 *
 * `aduRooms` are rooms in a legal ADU (functions/coLiving/aduUnit.js). They price private
 * for the same reason an ensuite room does — own bath, own entrance — but they are NOT on
 * `plans`: the ADU is a separate structure the main-house floor plan never drew, so
 * `countPrivateRooms` cannot see them.
 *
 * Mirror of functions/coLiving/coLivingGrossRent.js#deriveBathSplit.
 */
export const deriveBathSplit = (plans, beds, sharedRate, privateRate, aduRooms = 0) => {
    const adu = Math.max(0, num(aduRooms) ?? 0);
    const privateRooms = Math.min(countPrivateRooms(plans) + adu, num(beds) ?? 0);
    if (privateRooms <= 0) return null;

    // No fabricated premium: a "private" rate that isn't above shared is just shared.
    const rawPrivate = num(privateRate);
    if (rawPrivate == null || rawPrivate <= sharedRate) return null;

    return {
        privateRooms,
        sharedRooms: beds - privateRooms,
        privateRate: Math.round(rawPrivate),
        sharedRate: Math.round(sharedRate),
    };
};

/**
 * The calculator's rent model at the CURRENT room count, bath declarations, added baths
 * and rate overrides. One call answers everything the rent row renders.
 *
 * Added baths raise the ceiling for real (each full bath carries `maxBedsPerBath`
 * tenants), so "Baths added: 1" both clears the over-ceiling warning and can buy back the
 * beds an investor spent keeping an ensuite private. The old calculator spent that money
 * without ever earning the rent.
 *
 * @param {Object}  p
 * @param {Object}  p.interior            - propertyData.coLivingInterior
 * @param {number}  p.rooms               - current room count (user-editable)
 * @param {number}  [p.bathsAdded=0]      - full baths the investor plans to add
 * @param {Object}  [p.bathPlanOverrides] - calculatorInputs.coLivingBathPlan ({ roomId: plan })
 * @param {number}  [p.sharedRateOverride]
 * @param {number}  [p.privateRateOverride]
 * @returns {Object} see fields below; `blendedRate × rooms == monthlyGrossRent`
 */
export const resolveCoLivingRentSplit = ({
    interior,
    rooms,
    bathsAdded = 0,
    bathPlanOverrides = null,
    sharedRateOverride = null,
    privateRateOverride = null,
}) => {
    const cl = interior || {};
    const gr = cl.grossRent || {};
    const rr = cl.roomRate || {};
    const beds = num(rooms);
    const adds = Math.max(0, num(bathsAdded) ?? 0);

    // Baseline shared rate. When the pipeline's split fired, grossRent.ratePerRoom is
    // BLENDED — the true shared baseline is bathSplit.sharedRate. When it didn't fire,
    // ratePerRoom IS the shared rate. Never read the blend as if it were a room's rent.
    const pipelineShared = num(gr.bathSplit?.sharedRate) ?? num(gr.ratePerRoom) ?? num(rr.ratePerRoom);
    // Private median from the PadSplit comps (rateMeta), persisted whether or not the
    // split fired — so a house can earn a premium the pipeline didn't apply.
    const pipelinePrivate = num(gr.bathSplit?.privateRate) ?? num(gr.privateRate) ?? num(rr.privateRate);

    const sharedRate = num(sharedRateOverride) ?? pipelineShared;
    const privateRate = num(privateRateOverride) ?? pipelinePrivate;

    const pb = cl.projectedBedrooms || null;
    const plans = planBathsFor(cl, bathPlanOverrides);
    // The ceiling under the plans in force — NOT the one persisted at ingestion, which was
    // struck under the company default. Flip a bath to private and the house holds fewer
    // beds; read the stale ceiling instead and the premium comes free.
    // Legal-ADU rooms are served by the ADU's OWN bathroom, which the listing's bath count
    // does not include whenever the headline was main-only (exactly when `roomsAdded` is
    // non-zero). Without re-applying it here, every additive ADU trips `overBath` and the
    // ledger tells the buyer to "add a bathroom (~$5–15k)" for a room that has one.
    // Mirror of functions/coLiving/coLivingGrossRent.js.
    const aduBathRooms = Math.max(0, num(cl.aduUnit?.roomsAdded) ?? 0);
    const rawBathCeiling = bathCeilingFrom(plans, {
        maxBedsPerBath: num(pb?.maxBedsPerBath),
        bathsAdded: adds,
        fullBaths: num(pb?.fullBaths),
    });
    const bathCeiling = rawBathCeiling == null ? rawBathCeiling : rawBathCeiling + aduBathRooms;

    // How many rooms COULD be private here (⇒ whether the two-rate line is relevant at all),
    // and how many the declarations actually make private.
    const ensuiteCount = countEnsuiteBedrooms(cl.bathAccess);
    const hasPlan = pb != null && num(pb.ceilingWithConstruction ?? pb.ceiling) != null;

    // Baths HELD BACK from the whole-house shared pool — an en-suite (serves 1) OR a kept
    // Jack-and-Jill (serves 2). This is NOT `privateRooms`, which counts only en-suite
    // private-RATE bedrooms: a kept J&J earns no premium but STILL drops the bed ceiling, so
    // it's the reason the house is bath-capped. The over-ceiling note reads this to name the
    // cheap cure (a ~$1–2k hall door) instead of a ~$5–15k new bathroom.
    const keptOutOfPool = plans.filter((b) => (
        b && DECIDABLE_ACCESS.has(b.access) && b.plan === PLAN_PRIVATE && b.fixture !== 'half'
    )).length;

    const empty = {
        hasSplit: false, ensuiteCount, privateRooms: 0, sharedRooms: beds ?? 0, keptOutOfPool,
        privateRate, sharedRate, blendedRate: sharedRate, blendedRateExact: sharedRate,
        monthlyGrossRent: (beds != null && sharedRate != null) ? Math.round(beds * sharedRate) : null,
        bathCeiling, overBath: bathCeiling != null && beds != null && beds > bathCeiling,
        bathsAdded: adds,
    };
    if (beds == null || beds <= 0 || sharedRate == null || sharedRate <= 0) {
        return { ...empty, monthlyGrossRent: null };
    }
    // Legal-ADU rooms (aduUnit.js) price private on BOTH bases: their private bath is
    // asserted by the listing, not read off drawn door symbols, so a no-floor-plan deal
    // must still earn the premium. Clamped to `beds` inside deriveBathSplit, so dialling
    // the room count below the ADU's size can't charge a premium on rooms that are gone.
    const aduRooms = Math.max(0, num(cl.aduUnit?.premiumRooms) ?? 0);

    // The ENSUITE half of the split needs a legible plan (bathAccess comes from drawn
    // door symbols); the ADU half does not.
    if (!hasPlan && aduRooms <= 0) return empty;

    const split = deriveBathSplit(plans, beds, sharedRate, privateRate, aduRooms);
    if (!split) return empty;

    const monthlyGrossRent = Math.round(
        split.privateRooms * split.privateRate + split.sharedRooms * split.sharedRate
    );
    return {
        hasSplit: true,
        ensuiteCount,
        privateRooms: split.privateRooms,
        sharedRooms: split.sharedRooms,
        privateRate: split.privateRate,
        sharedRate: split.sharedRate,
        // Folded back into ONE rate so the single-rate SSOT holds for every downstream
        // surface (resolveCoLivingHeadlineRent, the deal feed, the deal page, the
        // matcher all quote beds × one rate). This is the DISPLAY/persist figure.
        blendedRate: Math.round(monthlyGrossRent / beds),
        // The same blend UNROUNDED, for feeding the returns model. Rounding the rate and
        // then re-multiplying by the room count reintroduces the rounding as real money:
        // 1×$1,000 + 7×$700 = $5,900, but 8 × round(737.5) = $5,904. Harmless when the
        // blend was invisible — but the split note now shows the investor the two rates,
        // so they can add them up themselves, and the ledger has to survive that check.
        blendedRateExact: monthlyGrossRent / beds,
        monthlyGrossRent,
        bathCeiling,
        overBath: bathCeiling != null && beds > bathCeiling,
        bathsAdded: adds,
        keptOutOfPool,
    };
};
